import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { CommonHeaderProps } from 'corgi-types';
import type { BoxShadowProps } from 'styled-system';
import { boxShadow } from 'styled-system';

import { Flex, FlexItem } from 'common/components/Flex';
import type { SearchOptions } from 'common/components/Search/hooks/types';
import Search from 'common/components/Search/Search';
import { GrowthbookFlag } from 'common/constants/feature-flags';
import { useFeatureFlagIsOn } from 'common/hooks/feature-flags';
import { useMatchesViewport } from 'common/hooks/media';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SearchParams } from 'common/types/search';
import { openSideNav } from 'common/utils/navigation';
import LanguageSwitch from './LanguageSwitch';
import Logo from './Logo/Logo';
import MenuButton from './MenuButton/MenuButton';
import NavigationLinks from './NavigationLinks/NavigationLinks';
import ToolbarActionItem from './ToolbarActionItem';
import { filterVisibleActionItems, getActionItems } from './utils';
import ZipCode from './ZipCode';

interface ToolbarProps extends CommonHeaderProps {
  searchOptions: SearchOptions;
  searchParams: SearchParams;
  isMinimized: boolean;
  isZipCode?: boolean;
  tabLabels?: string[];
}

const Wrapper = styled.section<BoxShadowProps>`
  ${boxShadow};
`;

const containerStyles: ComponentProps<typeof Flex> = {
  boxSizing: 'border-box',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: ['wrap', null, 'nowrap'],
  maxWidth: 1600,
  mx: 'auto',
  py: ['sp_16', 'sp_12'],
  px: ['sp_16', 'sp_24', 'sp_48', 'sp_64'],
};

const SEARCH_CONTAINER_MAX_WIDTH_WITH_TABS = '640px';

const searchContainerStyles: ComponentProps<typeof FlexItem> = {
  width: [1, 'auto'],
  order: [1, 0],
  flex: ['none', 1],
  maxWidth: [null, null, null, '920px'],
  mx: ['sp_0', 'sp_16', 'sp_20'],
};

const getSearchContainerStyles = (hasNavigationLinks: boolean) => ({
  ...searchContainerStyles,
  ...(hasNavigationLinks && {
    maxWidth: [null, null, SEARCH_CONTAINER_MAX_WIDTH_WITH_TABS],
  }),
});

const actionItemsStyles: ComponentProps<typeof FlexItem> = {
  as: 'nav',
  display: 'flex',
  justifyContent: 'flex-end',
};

const zipCodeContainerStyles: ComponentProps<typeof FlexItem> = {
  width: [1, null, 'auto'],
  order: [2, 1, 0],
  flexShrink: 0,
  mt: ['sp_12', null, 'sp_0'],
  ml: ['sp_0', null, 'sp_16'],
  mr: 'auto',
};

const ToolbarStartSection = ({
  isMinimized,
  withSideNavTabs,
  tabLabels = [],
}: {
  isMinimized: boolean;
  withSideNavTabs: boolean;
  tabLabels?: string[];
}) => (
  <Flex alignItems="center">
    {!isMinimized && <MenuButton onClick={() => openSideNav()} />}
    <Logo />
    {!isMinimized && withSideNavTabs && tabLabels.length > 0 && (
      <NavigationLinks tabLabels={tabLabels} />
    )}
  </Flex>
);

const Toolbar = ({
  customer,
  searchParams,
  searchOptions: { showIconOnMobile, withInputFilled = true },
  isMinimized = false,
  isZipCode = false,
  tabLabels = [],
  ...restProps
}: ToolbarProps) => {
  const { getLanguages, t } = useLocalization();
  const withSideNavTabs = useFeatureFlagIsOn(GrowthbookFlag.SideNavTabs);
  const isLoggedIn = !!customer?.isLoggedIn;
  const actionItemsProps = { customer, ...restProps };
  const actionItems = getActionItems(actionItemsProps);
  const {
    matches: { matchesTablet },
  } = useMatchesViewport(false);
  const showOnlySearchIcon = !matchesTablet && showIconOnMobile;
  const searchProps = {
    params: searchParams,
    withInputFilled,
  };
  const hasNavigationLinks = withSideNavTabs && tabLabels.length > 0;

  return (
    <Wrapper boxShadow="bottom">
      <Flex data-testid="toolbar" {...containerStyles}>
        <ToolbarStartSection
          isMinimized={isMinimized}
          withSideNavTabs={withSideNavTabs}
          tabLabels={tabLabels}
        />

        {isZipCode && (
          <FlexItem {...zipCodeContainerStyles}>
            <ZipCode />
          </FlexItem>
        )}
        {!isMinimized && (
          <FlexItem {...getSearchContainerStyles(hasNavigationLinks)}>
            {!showOnlySearchIcon && <Search {...searchProps} />}
          </FlexItem>
        )}
        <FlexItem {...actionItemsStyles} aria-label={t('h24_account_menu_label')}>
          {showOnlySearchIcon && <Search {...searchProps} showOnlySearchIcon />}
          {filterVisibleActionItems(actionItems, isLoggedIn, showOnlySearchIcon).map(item => (
            <ToolbarActionItem {...item} key={item.id} />
          ))}
          <LanguageSwitch languages={getLanguages()} />
        </FlexItem>
      </Flex>
    </Wrapper>
  );
};

export default Toolbar;
